/** Query param for block anchor ids when opening a document reference */
export const HTML_IDS_QUERY = "htmlIds";
/** Query param for unique match text when opening a document reference */
export const REF_MATCH_QUERY = "refMatch";
/** Query param for chunk text (disambiguation / storage parity with document links) */
export const REF_CHUNK_QUERY = "refChunk";
/** Forces highlight effect to re-run when repeating the same reference click (not read for highlight). */
export const REF_SCROLL_QUERY = "refScroll";
/**
 * Query term the document viewer seeds its in-document search with — set when a
 * global search result is opened so the searched word is marked and scrolled to.
 */
export const DOC_SEARCH_QUERY = "docSearch";

const REFERENCE_HIGHLIGHT_PARAM_KEYS = [
  HTML_IDS_QUERY,
  REF_MATCH_QUERY,
  REF_CHUNK_QUERY,
  REF_SCROLL_QUERY,
  DOC_SEARCH_QUERY,
] as const;

export function hasReferenceHighlightParams(
  searchParams: URLSearchParams
): boolean {
  return REFERENCE_HIGHLIGHT_PARAM_KEYS.some((key) =>
    Boolean(searchParams.get(key)?.trim())
  );
}

/** Returns new search string with reference-highlight keys removed; other params preserved. */
export function removeReferenceHighlightParamsFromSearchParams(
  searchParams: URLSearchParams
): string {
  const next = new URLSearchParams(searchParams);
  for (const key of REFERENCE_HIGHLIGHT_PARAM_KEYS) {
    next.delete(key);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : "";
}

export type ReferenceHighlightInUrl = {
  matchText?: string;
  chunkText?: string;
};

export function buildDocumentViewerQueryString(params: {
  htmlIds?: string[];
  matchText?: string;
  chunkText?: string;
  docSearch?: string;
}): string {
  const sp = new URLSearchParams();
  if (params.htmlIds?.length) {
    sp.set(HTML_IDS_QUERY, params.htmlIds.join(","));
  }
  if (params.matchText) {
    sp.set(REF_MATCH_QUERY, params.matchText);
  }
  if (params.chunkText) {
    sp.set(REF_CHUNK_QUERY, params.chunkText);
  }
  const docSearch = params.docSearch?.trim();
  if (docSearch) {
    sp.set(DOC_SEARCH_QUERY, docSearch);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

/** Appends the in-document search term to a path that may already carry a query string. */
export function appendDocSearchParamToPath(
  path: string,
  docSearch: string | undefined
): string {
  const term = docSearch?.trim();
  if (!term) {
    return path;
  }
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${DOC_SEARCH_QUERY}=${encodeURIComponent(term)}`;
}

/**
 * Reads htmlIds, optional ref match/chunk, and scroll target from the current URL.
 */
export function parseDocumentViewerHighlightParams(
  searchParams: URLSearchParams
): {
  highlightIds: string[];
  scrollToId: string | undefined;
  refMatchText: string | undefined;
  refChunkText: string | undefined;
  docSearchTerm: string | undefined;
} {
  const htmlIdsParam = searchParams.get(HTML_IDS_QUERY);
  let highlightIds: string[] = [];
  if (htmlIdsParam) {
    try {
      const decoded = decodeURIComponent(htmlIdsParam);
      highlightIds = decoded.split(",").filter(Boolean);
    } catch {
      highlightIds = [];
    }
  }

  return {
    highlightIds,
    scrollToId: highlightIds.length > 0 ? highlightIds[0] : undefined,
    refMatchText: searchParams.get(REF_MATCH_QUERY) ?? undefined,
    refChunkText: searchParams.get(REF_CHUNK_QUERY) ?? undefined,
    docSearchTerm: searchParams.get(DOC_SEARCH_QUERY)?.trim() || undefined,
  };
}
